/**
 * Token del dispositivo ricordato nel browser (una chiave per utente).
 * Il token vale solo insieme alla password: il server ne conserva l'impronta e lo accetta
 * solo per lo stesso utente, non revocato e non scaduto (rotta del core /api/twofa-trusted-device-login).
 */
export type KeyValueStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const PREFIX = 'flusia_2fa_device:';

export function trustedDeviceKey(userId: string): string {
  return `${PREFIX}${userId}`;
}

function defaultStorage(): KeyValueStorage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function saveTrustedDeviceToken(
  userId: string,
  token: string,
  expiresAt: string,
  storage: KeyValueStorage | null = defaultStorage(),
): void {
  if (!storage || !userId || !token) return;
  try {
    storage.setItem(trustedDeviceKey(userId), JSON.stringify({ token, expiresAt }));
  } catch {
    // archivio pieno o bloccato: la prossima volta si chiede il codice
  }
}

/** Token valido per l'utente, oppure null (scaduto, illeggibile o assente: in quei casi viene tolto). */
export function readTrustedDeviceToken(
  userId: string,
  now: Date = new Date(),
  storage: KeyValueStorage | null = defaultStorage(),
): string | null {
  if (!storage || !userId) return null;
  try {
    const raw = storage.getItem(trustedDeviceKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { token?: unknown; expiresAt?: unknown };
    const exp = typeof parsed.expiresAt === 'string' ? Date.parse(parsed.expiresAt) : NaN;
    if (typeof parsed.token !== 'string' || !parsed.token || !Number.isFinite(exp) || exp <= now.getTime()) {
      storage.removeItem(trustedDeviceKey(userId));
      return null;
    }
    return parsed.token;
  } catch {
    try {
      storage.removeItem(trustedDeviceKey(userId));
    } catch {
      // niente da fare
    }
    return null;
  }
}

export function clearTrustedDeviceToken(userId: string, storage: KeyValueStorage | null = defaultStorage()): void {
  if (!storage || !userId) return;
  try {
    storage.removeItem(trustedDeviceKey(userId));
  } catch {
    // niente da fare
  }
}
